import DPO from 'dpo8core';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { inject } from '@app/providers/QAU.ContextProvider';
import useImageProcessor from '@app/hooks/use-image-processor';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import { setTouchUpMode, setLastClickCoordinates, setAdvancedModeToleranceSliderEnabled } from '@app/actions/whiteInkOptionsActions';
import LocalizedText from '@LocalizedText';
import Button from '@components/buttons/Button';
import DefaultButton from '@components/buttons/default-btn/DefaultButton';
import Slider from '@components/slider/Slider';

export const AdvancedMode = ({
  flowConfig,
  context,
  includesWhiteUnderprint,
  lastClickCoordinates,
  toleranceSliderEnabled,
  originalImageData,
  whiteInkOptionsImageData,
  touchUpMode,
  setTouchUpMode,
  updateImageOnPreview,
  setAdvancedModeImageData,
  setLastClickCoordinates,
  setAdvancedModeToleranceSliderEnabled,
  getNaturalImageCoordinates,
  startLoading,
  endLoading,
}) => {
  const [filterParams, setFilterParams] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [pixelCoordinates, setPixelCoordinates] = useState(null);
  const [tolerance, setTolerance] = useState(0);
  const [minTolerance, setMinTolerance] = useState(0);
  const [maxTolerance, setMaxTolerance] = useState(0);
  const [isApplyHidden, setIsApplyHidden] = useState(true);
  const [isChangesApplied, setIsChangesApplied] = useState(null);

  const { processedImageData, processing } = useImageProcessor(whiteInkOptionsImageData, filterParams, filterType);
  const analyzer = useMemo(() => new DPO.project.ImageAnalyzer(context, flowConfig), [context, flowConfig]);

  const resetToleranceValues = useCallback(() => {
    const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');
    const max = +removeWhiteFeature.getParamValue('maxTolerance');
    const min = +removeWhiteFeature.getParamValue('minTolerance');
    const defaultValue = +removeWhiteFeature.getParamValue('defaultTolerance');

    setAdvancedModeToleranceSliderEnabled(false);
    setIsApplyHidden(true);
    setMinTolerance(min);
    setMaxTolerance(max);
    setTolerance(defaultValue);
  }, [flowConfig, setMinTolerance, setMaxTolerance, setTolerance]);

  const toggleTouchUpMode = event => {
    const { currentTarget: { dataset: { touchUpMode: newTouchUpMode } } = {} } = event;

    setTouchUpMode(touchUpMode !== newTouchUpMode ? newTouchUpMode : null);
  };

  const resetTouchUpChanges = () => {
    updateImageOnPreview(whiteInkOptionsImageData);
    setLastClickCoordinates(null);
    setPixelCoordinates(null);
    resetToleranceValues();
  };

  const onChangeTolerance = value => {
    setTolerance(value);
  };

  const onApplyClicked = () => {
    setIsApplyHidden(true);
    setIsChangesApplied(true);
    setAdvancedModeImageData(processedImageData);
    setLastClickCoordinates(null);
    setPixelCoordinates(null);
    resetToleranceValues();
  };

  useEffect(() => {
    processing ? startLoading() : endLoading();
  }, [processing, startLoading, endLoading]);

  useEffect(() => {
    if (!lastClickCoordinates || !includesWhiteUnderprint) return;

    setIsChangesApplied(false);
    setPixelCoordinates(getNaturalImageCoordinates(lastClickCoordinates));
  }, [lastClickCoordinates]);

  useEffect(() => {
    if (!processedImageData || isChangesApplied || !pixelCoordinates) return;
    updateImageOnPreview(processedImageData);
    setIsApplyHidden(false);
  }, [processedImageData]);

  useEffect(() => {
    switch(touchUpMode) {
      case 'remove':
        resetTouchUpChanges();
        setFilterType('FillColor');
        break;
      case 'restore':
        resetTouchUpChanges();
        setFilterType('RestoreOriginalPixels');
        break;
      default:
        resetTouchUpChanges();
        setIsApplyHidden(true);
    }
  }, [touchUpMode]);

  useEffect(() => {
    if (!pixelCoordinates) return;
    const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');
    const isPixelInWhiteRange = analyzer.isPixelInWhiteRange(
      touchUpMode === 'remove' ? whiteInkOptionsImageData.rgb : originalImageData.rgb,
      pixelCoordinates,
    );

    if (!isPixelInWhiteRange) {
      setIsApplyHidden(true);
      setAdvancedModeToleranceSliderEnabled(false);
      updateImageOnPreview(whiteInkOptionsImageData);
      return;
    }

    setAdvancedModeToleranceSliderEnabled(true);
    setFilterParams({
      minWhiteRgbSum: context.getMinWhiteRgbSum(tolerance),
      minSingleChannel: context.getMinSingleChannel(tolerance),
      featheringRadius: tolerance !== minTolerance && +removeWhiteFeature.getParamValue('featheringRadius'),
      coordinates: pixelCoordinates,
      originalImageData,
      toColor: [0, 0, 0, 0],
    });
  }, [context, flowConfig, tolerance, minTolerance, pixelCoordinates]);

  useEffect(() => {
    return () => {
      !isChangesApplied && updateImageOnPreview(whiteInkOptionsImageData);
      setLastClickCoordinates(null);
    }
  }, [isChangesApplied]);

  useEffect(() => {
    resetToleranceValues();
  }, [flowConfig]);

  return (
    <div className="qau-advanced-mode">
      <LocalizedText id="qau-white-options-touch-up-title-text" tagName="span" />
      <Button className={classnames('qau-touch-up-options-button', { selected: touchUpMode === 'remove' })} data-touch-up-mode="remove" onClick={toggleTouchUpMode}>
        <LocalizedText id="qau-white-options-touch-up-remove-white-btn" tagName="span" />
      </Button>
      <Button className={classnames('qau-touch-up-options-button', { selected: touchUpMode === 'restore' })} data-touch-up-mode="restore" onClick={toggleTouchUpMode}>
        <LocalizedText id="qau-white-options-touch-up-restore-white-btn" tagName="span" />
      </Button>
      <Slider
        step={1}
        defaultValue={tolerance}
        minValue={minTolerance}
        maxValue={maxTolerance}
        onFinishChanging={onChangeTolerance}
        onChangeValueByStep={onChangeTolerance}
        minValueLocaleKey="qau-white-tolerance-less-title-text"
        maxValueLocaleKey="qau-white-tolerance-more-title-text"
        ariaLabelLocaleKey="qau-white-tolerance-slider-aria-label"
        disabled={!toleranceSliderEnabled}
      />
      <div className={classnames('qau-advanced-mode-apply-button-wrapper', { hidden: isApplyHidden })}>
      <LocalizedText id="qau-advanced-mode-apply-text" tagName="span" />
        <DefaultButton
          className={classnames('qau-advanced-mode-apply-button')}
          locKey="qau-advanced-mode-apply-btn"
          onClick={onApplyClicked}
        />
      </div>
    </div>
  )
}

const mapStateToProps = ({
  whiteInkOptions: {
    includesWhiteUnderprint,
    lastClickCoordinates,
    artworkPreview,
    advancedMode: {
      touchUpMode,
      toleranceSliderEnabled,
    }
  },
}) => ({
  includesWhiteUnderprint,
  artworkPreview,
  touchUpMode,
  lastClickCoordinates,
  toleranceSliderEnabled,
});

const mapDispatchToProps = {
  setTouchUpMode,
  setLastClickCoordinates,
  setAdvancedModeToleranceSliderEnabled,
  startLoading,
  endLoading,
};

AdvancedMode.propTypes = {
  flowConfig: PropTypes.object.isRequired,
  context: PropTypes.object,
  includesWhiteUnderprint: PropTypes.bool.isRequired,
  lastClickCoordinates: PropTypes.object,
  toleranceSliderEnabled: PropTypes.bool.isRequired,
  originalImageData: PropTypes.object,
  whiteInkOptionsImageData: PropTypes.object,
  touchUpMode: PropTypes.string,
  setAdvancedModeImageData: PropTypes.func.isRequired,
  setLastClickCoordinates: PropTypes.func.isRequired,
  setAdvancedModeToleranceSliderEnabled: PropTypes.func.isRequired,
  getNaturalImageCoordinates: PropTypes.func.isRequired,
  updateImageOnPreview: PropTypes.func.isRequired,
  setTouchUpMode: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  endLoading: PropTypes.func.isRequired,
};

export default inject('flow', 'flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(AdvancedMode));